/**
 * FindingsStrip — append-only results bar at the bottom of Node Inspector.
 * Populated by granular action results (entity lookup, sanctions, exchange, etc.).
 * Sits below the tab content, outside the scrollable area — always visible.
 *
 * It is collapsible because it is `shrink-0` in a column that is already short:
 * expanded it takes ~110px off the tab content, which on a laptop window was
 * more than the content area itself had. Collapsed it keeps the ~20px header so
 * the findings are still announced, with a count, and one click away.
 */
import React, { useCallback, useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import type { InspectorTab } from './types';
import type { GranularAction } from './GranularActionsRow';

export interface Finding {
    id: string;
    action: GranularAction;
    emoji: string;
    label: string;
    summary: string;
    time: string;                     // HH:MM
    variant: 'success' | 'warning' | 'error' | 'info';
    tabLink?: InspectorTab;           // clicking the finding jumps to this tab
}

interface FindingsStripProps {
    findings: Finding[];
    onClear: () => void;
    onTabLink?: (tab: InspectorTab) => void;
}

const VARIANT_STYLES: Record<Finding['variant'], string> = {
    success: 'text-emerald-600 dark:text-emerald-400',
    warning: 'text-amber-600 dark:text-amber-400',
    error:   'text-red-500',
    info:    'text-primary',
};

const COLLAPSE_KEY = 'workbench-inspector-findings-collapsed';

export const FindingsStrip: React.FC<FindingsStripProps> = ({ findings, onClear, onTabLink }) => {
    const [collapsed, setCollapsed] = useState(() => {
        if (typeof localStorage === 'undefined') return false;
        return localStorage.getItem(COLLAPSE_KEY) === 'true';
    });

    const toggle = useCallback(() => {
        setCollapsed(prev => {
            const next = !prev;
            try {
                localStorage.setItem(COLLAPSE_KEY, String(next));
            } catch {
                // Private-browsing quota errors must not break the panel.
            }
            return next;
        });
    }, []);

    if (findings.length === 0) return null;

    return (
        <div className="border-t border-border bg-secondary/20 shrink-0" data-testid="wb-inspector-findings">
            <div className="flex items-center justify-between px-2 py-1 border-b border-border/50">
                <button
                    type="button"
                    onClick={toggle}
                    aria-expanded={!collapsed}
                    title={collapsed ? 'Show findings' : 'Collapse findings to free up room for the tab content'}
                    className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/70 hover:text-foreground transition-colors"
                >
                    {collapsed ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    Findings
                    {collapsed && (
                        <span className="normal-case tracking-normal text-muted-foreground/60">· {findings.length}</span>
                    )}
                </button>
                <button
                    onClick={onClear}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    title="Clear findings"
                >
                    <X className="w-3 h-3" />
                </button>
            </div>
            <div className={`max-h-[90px] overflow-y-auto styled-scrollbar ${collapsed ? 'hidden' : ''}`}>
                {findings.map(f => (
                    <div
                        key={f.id}
                        onClick={() => f.tabLink && onTabLink?.(f.tabLink)}
                        className={`flex items-center gap-1.5 px-2 py-1 text-[11px] border-b border-border/30 last:border-0
                            ${f.tabLink ? 'cursor-pointer hover:bg-muted/40 transition-colors' : ''}
                        `}
                    >
                        <span className="shrink-0 text-[12px] leading-none">{f.emoji}</span>
                        <span className={`font-medium shrink-0 ${VARIANT_STYLES[f.variant]}`}>{f.label}</span>
                        <span className="text-muted-foreground truncate flex-1">{f.summary}</span>
                        <span className="text-[10px] text-muted-foreground/50 shrink-0 tabular-nums">{f.time}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};
